import { unwrap } from "@sagot/api-client";
import { MAX_IP_ALLOWLIST, SESSION_TIMEOUTS, type SessionTimeoutKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Select, Switch, Textarea, toast } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { wsKeys, type SecurityOut } from "../../shared";
import { invalidEntries, isIpOrCidr, parseList } from "../settings-model";
import { useInvalidate, useSecuritySettings } from "../use-settings";
import { AllowedEmailDomains } from "./allowed-email-domains";
import { CardActions, CardSkeleton, PanelError, useCanManage } from "./panel-kit";
import { SsoCard } from "./sso-card";

interface Draft {
  require2fa: boolean;
  domains: string[];
  timeout: SessionTimeoutKey;
  ips: string;
}

const toDraft = (s: SecurityOut): Draft => ({ require2fa: s.require_2fa, domains: [...s.allowed_email_domains], timeout: s.session_timeout, ips: s.ip_allowlist.join("\n") });

/** Draft → request body, or the field errors that block saving. Domains arrive validated from the list editor. */
export function validateSecurity(draft: Draft): { errors: { ips?: string[] }; body: { require_2fa: boolean; allowed_email_domains: string[]; session_timeout: SessionTimeoutKey; ip_allowlist: string[] } } {
  const ips = parseList(draft.ips);
  const badIps = invalidEntries(ips, isIpOrCidr);
  return {
    errors: badIps.length ? { ips: badIps } : {},
    body: { require_2fa: draft.require2fa, allowed_email_domains: draft.domains, session_timeout: draft.timeout, ip_allowlist: ips },
  };
}

function SignInRules({ security }: { security: SecurityOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const invalidate = useInvalidate();
  const manage = useCanManage("settings.manage");
  const [draft, setDraft] = useState<Draft>(() => toDraft(security));
  const [errors, setErrors] = useState<{ ips?: string[] }>({});
  const [busy, setBusy] = useState(false);
  const save = async (event: SyntheticEvent) => {
    event.preventDefault();
    const result = validateSecurity(draft);
    setErrors(result.errors);
    if (result.errors.ips) return;
    setBusy(true);
    try {
      await unwrap(api.PUT("/v1/settings/security", { body: result.body }));
      toast.success(t("settings.security.saved"));
      await invalidate(wsKeys.security(wsId));
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };
  const locked = !manage.allowed;
  return (
    <form className="card" onSubmit={(event) => void save(event)} noValidate aria-labelledby="signin-title">
      <div className="card-header">
        <h2 id="signin-title" className="card-title">
          {t("settings.security.title")}
        </h2>
      </div>
      <fieldset className="divide-y divide-line" disabled={locked}>
        <div className="flex items-center justify-between gap-4 px-5 py-4">
          <div>
            <div id="require-2fa" className="text-sm font-semibold">
              {t("settings.security.require2fa")}
            </div>
            <div className="text-xs text-fg-muted">
              {security.members_without_2fa > 0 ? t("settings.security.membersWithout2fa", { count: security.members_without_2fa }) : t("settings.security.allHave2fa")}
            </div>
          </div>
          <Switch checked={draft.require2fa} onCheckedChange={(checked) => { setDraft((d) => ({ ...d, require2fa: checked })); }} aria-labelledby="require-2fa" />
        </div>
        <div className="px-5 py-4">
          <AllowedEmailDomains domains={draft.domains} onChange={(domains) => { setDraft((d) => ({ ...d, domains })); }} disabled={locked} />
        </div>
        <div className="grid grid-cols-1 gap-4 px-5 py-4 md:grid-cols-2">
          <Field label={t("settings.security.timeout")}>
            <Select value={draft.timeout} onChange={(event) => { setDraft((d) => ({ ...d, timeout: event.target.value as SessionTimeoutKey })); }}>
              {SESSION_TIMEOUTS.map((key) => (
                <option key={key} value={key}>
                  {t(`settings.security.timeouts.${key}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label={`${t("settings.security.ipAllowlist")} ${t("common.optional")}`}
            hint={t("settings.security.ipHint", { count: MAX_IP_ALLOWLIST })}
            error={errors.ips ? t("settings.security.ipInvalid", { items: errors.ips.join(", ") }) : undefined}
          >
            <Textarea rows={2} className="font-mono text-xs" placeholder={t("settings.security.ipPlaceholder")} value={draft.ips} onChange={(event) => { setDraft((d) => ({ ...d, ips: event.target.value })); }} />
          </Field>
        </div>
      </fieldset>
      <CardActions>
        <Button type="submit" disabled={locked} loading={busy} title={manage.reason}>
          {t("settings.save")}
        </Button>
      </CardActions>
    </form>
  );
}

/** Security panel: sign-in rules (2FA, domains, timeout, IP allowlist) + single sign-on. */
export function SecurityPanel() {
  const security = useSecuritySettings();
  if (security.isPending) return <CardSkeleton rows={4} />;
  if (security.isError) return <PanelError error={security.error} onRetry={() => void security.refetch()} />;
  return (
    <>
      <SignInRules key={security.dataUpdatedAt} security={security.data} />
      <SsoCard />
    </>
  );
}
